import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSupabaseClient } from '../../lib/supabase';
import { Footer } from '@ui/components/Footer';
import { SeekerHeader } from '@ui/components/SeekerHeader';
import { ChevronDownIcon, CheckmarkIcon, ArrowRightIcon, ChevronLeftIcon, ChevronRightIcon } from '../icons';

type TermsSection = {
  id: string;
  label: string;
  body: string;
};

function ChevronIcon({ expanded }: { expanded: boolean }) {
  return (
    <ChevronDownIcon
      className={`transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
    />
  );
}

const TermsOfServicePage: React.FC = () => {
  const navigate = useNavigate();
  const [accepted, setAccepted] = useState(false);
  const [termsSections, setTermsSections] = useState<TermsSection[]>([]);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch terms sections from database on mount
  useEffect(() => {
    const fetchTerms = async () => {
      try {
        const supabase = getSupabaseClient();
        const { data, error } = await supabase
          .from('terms_content')
          .select('item_id, body, label')
          .eq('audience', 'seeker')
          .eq('item_type', 'important')
          .order('sort_order', { ascending: true });

        if (error) {
          console.error('Error fetching terms:', error);
          setError('Failed to load terms of service. Please refresh the page.');
          return;
        }

        if (data) {
          const sections: TermsSection[] = data.map((item) => ({
            id: item.item_id,
            label: item.label || 'Terms',
            body: item.body,
          }));
          setTermsSections(sections);
        }
      } catch (err) {
        console.error('Unexpected error fetching terms:', err);
        setError('An unexpected error occurred. Please refresh the page.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTerms();
  }, []);

  const handleContinue = useCallback(() => {
    if (!accepted) return;
    navigate('/onboarding/finding-match');
  }, [navigate, accepted]);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleViewFullTerms = () => {
    navigate('/terms/full');
  };

  const toggleSection = (id: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen w-full flex-col lg:overflow-auto overflow-hidden">
        <div className="relative flex flex-1 w-full flex-col bg-onboarding-content-bg items-center justify-center">
          <div className="text-center">
            <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-onboarding-primary border-r-transparent" />
            <p className="text-sm text-onboarding-primary/70">Loading terms...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen w-full flex-col lg:overflow-auto overflow-hidden">
        <div className="relative flex flex-1 w-full flex-col bg-onboarding-content-bg items-center justify-center">
          <div className="max-w-md rounded-2xl border border-red-300 bg-red-50 p-6 text-center">
            <p className="mb-2 text-sm font-semibold text-red-900">Error loading terms</p>
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen w-full flex-col lg:overflow-auto overflow-hidden">
      <div className="relative flex flex-1 w-full flex-col bg-onboarding-content-bg">
        {/* Mobile header */}
        <header className="flex lg:hidden h-16 shrink-0 items-center border-b border-onboarding-bg/25 bg-onboarding-header px-4">
          <button
            type="button"
            onClick={handleBack}
            aria-label="Go back"
            className="flex w-10 items-center justify-center text-onboarding-text-light transition-opacity hover:opacity-70"
          >
            <ChevronLeftIcon />
          </button>
          <div className="flex flex-1 justify-center">
            <img src="/gclogo.webp" alt="Guides Collective" className="h-8 w-auto" />
          </div>
          <div className="w-10" aria-hidden />
        </header>

        {/* Desktop header */}
        <div className="hidden lg:block">
          <SeekerHeader />
        </div>

        {/* Content wrapper */}
        <div className="flex flex-1 flex-col bg-onboarding-content-bg lg:items-center lg:px-[72px] lg:py-[40px] lg:gap-[40px]">
          {/* Main content area */}
          <main className="flex flex-1 lg:flex-initial min-h-0 flex-col gap-6 lg:gap-[24px] overflow-y-auto lg:overflow-visible px-4 lg:px-0 pb-10 lg:pb-[40px] pt-6 lg:pt-0 scroll-smooth w-full lg:w-[509px]">
          {/* Title */}
          <h1 className="font-recoleta text-[32px] font-normal leading-[1.1] text-onboarding-card-green">
            Terms of Service
          </h1>

          {/* Subtitle */}
          <p className="font-helvetica text-lg leading-[1.4] text-onboarding-text-dark">
            Please review the important statements below before accepting.
          </p>

          {/* View full terms link */}
          <button
            onClick={handleViewFullTerms}
            className="flex items-center gap-2 text-left font-helvetica text-lg leading-[1.4] text-onboarding-card-green hover:opacity-80 transition-opacity"
          >
            <span>View full terms of service</span>
            <ArrowRightIcon />
          </button>

          {/* Intro text (no border) */}
          <div className="flex flex-col gap-1 text-onboarding-card-green">
            <p className="font-helvetica text-base font-bold leading-[1.6]">
              Please review these statements carefully as they require your acknowledgement.
            </p>
            <p className="font-helvetica text-sm font-normal leading-[1.4]">
              Welcome to the Guides Collective Seeker experience. Before we get started, please review these statements carefully before accepting. Agreeing to these acknowledgements, as well as the Terms of Service available here, are a condition for your registering as a Seeker on our platform. If you do not agree, you are not permitted to use the platform.
            </p>
          </div>

          {/* Accordion sections */}
          <div className="flex flex-col gap-4">
            {termsSections.map((section) => (
              <div
                key={section.id}
                className="border border-onboarding-card-green rounded-2xl overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => toggleSection(section.id)}
                  className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left"
                >
                  <span className="font-helvetica text-base font-bold leading-[1.6] text-onboarding-card-green">
                    {section.label}
                  </span>
                  <span className="text-onboarding-card-green">
                    <ChevronIcon expanded={expandedSections[section.id]} />
                  </span>
                </button>
                {expandedSections[section.id] && (
                  <div className="px-4 pb-3">
                    <p className="font-helvetica text-sm leading-[1.4] text-onboarding-card-green">
                      {section.body}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Checkbox */}
          <label className="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="sr-only"
            />
            <div className="flex items-center py-[3px] shrink-0">
              <span
                aria-hidden
                className={`flex h-6 w-6 items-center justify-center rounded-[6px] border transition ${
                  accepted
                    ? 'border-onboarding-card-green bg-onboarding-card-green'
                    : 'border-onboarding-radio-border'
                }`}
              >
                {accepted && (
                  <CheckmarkIcon className="h-3 w-3 text-white" />
                )}
              </span>
            </div>
            <span className="font-helvetica text-base leading-[1.6] tracking-[-0.08px] text-onboarding-text-dark">
              I accept the Terms of Service
            </span>
          </label>

          {/* CTA Buttons */}
          <div className="flex items-center gap-3 py-4">
            <button
              type="button"
              onClick={handleBack}
              aria-label="Go back"
              className="flex h-[54px] w-[54px] shrink-0 items-center justify-center rounded-full border border-onboarding-card-green bg-transparent transition-colors hover:bg-onboarding-card-green/5"
            >
              <ChevronLeftIcon className="text-onboarding-card-green" />
            </button>
            <button
              type="button"
              onClick={handleContinue}
              disabled={!accepted}
              className={`flex flex-1 h-[54px] items-center justify-center gap-2 rounded-full px-5 py-[18px] font-helvetica text-base leading-[1.6] tracking-[-0.08px] transition-colors ${
                accepted
                  ? 'bg-onboarding-card-green text-onboarding-content-bg hover:bg-onboarding-card-green/90'
                  : 'bg-onboarding-radio-border text-onboarding-content-bg cursor-not-allowed'
              }`}
            >
              <span>Continue</span>
              <ChevronRightIcon />
            </button>
          </div>
          </main>
        </div>
      </div>

      {/* Footer - desktop only */}
      <div className="hidden lg:block">
        <Footer />
      </div>
    </div>
  );
};

export default TermsOfServicePage;
